import React, { useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Box,
  Typography,
  Container,
  Paper,
  Divider
} from '@material-ui/core';
import Image from '../common/Image';
import headerImage from '../../img/header_image.jpg';

import { getUserList, getLatestUserList } from "../../actions/profileActions";

import Pagination from "../common/Pagination";
import Spinner from "../common/Spinner";
import BusinessProfileCard from "./BusinessProfileCard";
import BusinessProfileCardMin from "./BusinessProfileCardMin";

import AdNZWorksLarge from "../common/ads/AdNZWorksLarge";
import AdNZCafemap from "../common/ads/AdNZCafemap";
import { getNewNotificationNum } from "../../utils/getNotificationNum";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
  },
  pageHeader: {
    height: '30rem',
    backgroundImage: `url(${headerImage})`,
    backgroundSize: 'cover',
    marginBottom: '5rem',
    borderBottom: '2px solid rgb(5, 125, 237)',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    color: 'white',
    display: 'inline-block',
    fontSize: '4rem',
    padding: '2rem',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    borderRadius: '10px',
    border: '1px solid black',
    textAlign: 'center',
    lineHeight: 1.2,
    [theme.breakpoints.down('sm')]: {
      fontSize: '2.6rem',
      padding: '1.5rem',
    },
  },
  mobileBreak: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  desktopText: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  mainContainer: {
    margin: '0 5rem',
    [theme.breakpoints.down('md')]: {
      margin: '0 2.5rem',
    },
    [theme.breakpoints.down('sm')]: {
      margin: '0 1rem',
    },
  },
  warningBox: {
    marginBottom: '3rem',
    padding: theme.spacing(2),
    backgroundColor: '#fadeec',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.error.main}`,
  },
  warningText: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  warningSubText: {
    color: theme.palette.text.secondary,
    fontSize: '1.4rem',
  },
  infoText: {
    color: theme.palette.error.main,
    fontSize: '1.4rem',
    marginBottom: theme.spacing(2),
  },
  mainContent: {
    display: 'grid',
    gridTemplateColumns: '3fr 1fr',
    gap: '2rem',
    minHeight: '80vh',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      gap: '1rem',
    },
  },
  sideSection: {
    minWidth: '30rem',
    margin: '1rem',
    [theme.breakpoints.down('md')]: {
      minWidth: 'auto',
      width: '100%',
      margin: '1rem 0',
    },
  },
  sideSectionTitle: {
    padding: '0.5rem',
    border: '1px solid rgb(32, 156, 238)',
    borderRadius: '5px',
    color: 'rgb(5, 125, 237)',
    backgroundColor: 'rgba(32, 156, 238, 0.1)',
    fontSize: '1.6rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  latestProfilesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginTop: '1rem',
  },
  latestProfileLink: {
    textDecoration: 'none',
    display: 'block',
  },
  paginationContainer: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'center',
  },
}));

const DEFAULT_PAGE_SIZE = 24;

const BusinessProfileList = () => {
  const classes = useStyles();
  const profileList = useSelector(state => state.profile.profileList);
  const latestProfileList = useSelector(
    state => state.profile.latestProfileList
  );
  const itemCount = useSelector(state => state.profile.itemCount);
  const loading = useSelector(state => state.common.loading);
  const currentUser = useSelector(state => state.user.currentUser);
  const notifications = useSelector(state => state.user.notifications);
  const userType = currentUser ? currentUser.profile.user_type : null;
  let notificationNum = getNewNotificationNum(notifications);

  const adCount = 3 - (profileList.length % 3);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getUserList(1, DEFAULT_PAGE_SIZE));
    dispatch(getLatestUserList());
  }, [dispatch]);

  const handlePageClick = selectedPage => {
    dispatch(getUserList(selectedPage, DEFAULT_PAGE_SIZE));
  };

  const renderHowTo = () => {
    return (
      <Paper className={classes.warningBox} elevation={1}>
        <Typography className={classes.warningText}>
          企業紹介一覧にアカウント情報を表示する為には、メイン画像の設定及び企業紹介文を記述する必要が有ります。
        </Typography>
        <Typography className={classes.warningSubText}>
          画面右上の『メニュー』から『企業紹介編集』を選択し、各変更を行ってください。
        </Typography>
      </Paper>
    );
  };

  return (
    <Box className={classes.root}>
      <Helmet>
        <title>
          {notificationNum ? `(${notificationNum})` : ""}
          登録企業・雇用者プロフィール
        </title>
        <meta
          name="description"
          content="ニュージーワークスに登録されている企業・雇用者の一覧です。（画像と紹介文が設定されているアカウントのみ表示されます）"
        />
      </Helmet>

      <Box className={classes.pageHeader}>
        <Typography variant="h1" className={classes.headerTitle}>
          <span className={classes.desktopText}>登録企業・雇用者 プロフィール</span>
          <span className={classes.mobileBreak}>
            登録企業・雇用者<br />プロフィール
          </span>
        </Typography>
      </Box>

      <Container className={classes.mainContainer}>
        {userType === "Business" ? (
          renderHowTo()
        ) : (
          <Typography className={classes.infoText}>
            ※企業紹介文及びメイン画像が設定されているアカウントのみ表示されます
          </Typography>
        )}

        <Box className={classes.mainContent}>
          <Box>
            {loading ? (
              <Spinner cover={true} />
            ) : (
              <Grid container spacing={3}>
                {profileList.map(user => (
                  <Grid item xs={12} sm={6} md={4} key={user._id}>
                    <Link to={`/profiles/${user._id}`} style={{ textDecoration: 'none' }}>
                      <BusinessProfileCard user={user} />
                    </Link>
                  </Grid>
                ))}
              </Grid>
            )}
            <Box className={classes.paginationContainer}>
              <Pagination
                itemCount={itemCount}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={handlePageClick}
                className="bg-secondary-light"
              />
            </Box>
          </Box>

          <Box className={classes.sideSection}>
            <Typography variant="h3" className={classes.sideSectionTitle}>
              最近更新があったアカウント
            </Typography>
            <Box className={classes.latestProfilesContainer}>
              {latestProfileList && latestProfileList.map(user => (
                <Link
                  key={user._id}
                  to={`/profiles/${user._id}`}
                  className={classes.latestProfileLink}
                >
                  <BusinessProfileCardMin user={user} />
                </Link>
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default BusinessProfileList;
